import onetime from "@-/com.npmjs.onetime";
import { PostHog } from "@-/com.npmjs.posthog-react-native";
import { Platform } from "@-/com.npmjs.react-native";
import { connectAnalyticsSink } from "@-/dev.improve.haniverse.lib.analytics";
import { registerBeforeRestart } from "@-/dev.improve.haniverse.lib.app.restart";
import { getStorage } from "@-/dev.improve.haniverse.lib.storage";
import Constants from "@-/com.npmjs.expo-constants";
import * as Updates from "@-/com.npmjs.expo-updates";

/** One client for events and flags, with the same durable storage as the app. */
export const getPostHog = onetime(() => {
  const apiKey = process.env.EXPO_PUBLIC_POSTHOG_API_KEY;
  const isServer = Platform.OS === "web" && globalThis.window === undefined;
  const disabled = !apiKey || isServer || process.env.NODE_ENV === "test";
  const client = new PostHog(apiKey || "posthog-unconfigured", {
    disabled,
    host: process.env.EXPO_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    // Expo's standard property names link events back to the exact EAS release.
    // App properties survive PostHog.reset(), unlike register() super-properties.
    customAppProperties: (properties) => ({
      ...properties,
      app_variant: process.env.EXPO_PUBLIC_APP_VARIANT ?? "unknown",
      "eas/account": Constants.expoConfig?.owner ?? null,
      "eas/channel": Updates.channel ?? null,
      "eas/project_id": Constants.easConfig?.projectId ?? null,
      "eas/runtime_version": Updates.runtimeVersion ?? null,
      "eas/update_id": Updates.updateId ?? null,
    }),
    customStorage: disabled
      ? undefined
      : {
          getItem: (key) => getStorage().getString(`posthog.${key}`) ?? null,
          setItem: (key, value) => getStorage().set(`posthog.${key}`, value),
        },
    persistence: disabled ? "memory" : "file",
    // Most new learners are local accounts with no server user, identified only much later (if
    // ever). Profiles from the first open let funnels and retention start at the welcome screen
    // and carry their history into the account when they do sign up.
    personProfiles: "always",
    // Identity is settled before requesting flags. Never enable a feature from
    // another account's persisted flag cache during startup.
    captureAppLifecycleEvents: !disabled,
    disableSurveys: true,
    preloadFeatureFlags: false,
    // Replay records iOS/Android only (web would need posthog-js). Debug builds are excluded:
    // their frame timings are not representative, and local sessions are not worth recording.
    // Sampling and minimum duration are set remotely in the project; a sample rate of 0 is the
    // kill switch (no release needed).
    //
    // Store (production) builds never record: App Store guideline 2.5.14 requires explicit consent
    // and a visible recording indicator before recording the screen, and the app asks for neither.
    // Replay stays on for the internal staging/alpha TestFlight builds.
    enableSessionReplay:
      !disabled &&
      Platform.OS !== "web" &&
      !__DEV__ &&
      process.env.EXPO_PUBLIC_APP_VARIANT !== "production",
    sessionReplayConfig: {
      // In the native SDKs this masks ALL text, which would grey out every card. Sensitive views
      // (credentials, emails, names, notes, avatars) are wrapped in `PostHogMaskView` instead;
      // password fields stay masked regardless.
      maskAllTextInputs: false,
      // The app's images are curriculum content; the only user-supplied image (the profile
      // avatar) is masked explicitly.
      maskAllImages: false,
      maskAllSandboxedViews: true,
      // Native logs can carry personal data; network telemetry is metadata only (iOS).
      captureLog: false,
      captureNetworkTelemetry: true,
      throttleDelayMs: 1000,
    },
  });
  connectAnalyticsSink({
    capture: (event, properties, options) => client.capture(event, properties, options),
    getDistinctId: () => client.getDistinctId(),
    register: (properties) => client.register(properties),
    setPersonProperties: (set, setOnce, reloadFeatureFlags) =>
      client.setPersonProperties(set, setOnce, reloadFeatureFlags),
  });
  // Queued events would otherwise wait for the next launch (or be lost with an OTA swap).
  registerBeforeRestart("posthog-flush", () => client.flush());
  return client;
});
